
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Card from '../../components/base/Card';
import Button from '../../components/base/Button';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { mockItems, categories } from '../../mocks/items';

interface Item {
  id: string;
  title: string;
  description: string;
  category: string;
  dateAdded: string;
  image: string;
  tags: string[];
  isFavorite: boolean;
  notes: string;
  price?: string;
  priority: 'low' | 'medium' | 'high';
  amazonUrl?: string;
  amazonAsin?: string;
  websiteSource?: string;
  deletedAt?: string;
  rating?: number;
  reviewCount?: number;
  checkoutUrl?: string;
  purchased?: boolean;
  purchasedAt?: string;
}

// Amazon's Most Gifted Items (Updated from https://www.amazon.com/gp/most-gifted)
const amazonMostGiftedData = [
  {
    rank: 1,
    name: 'Apple AirPods Pro (2nd Generation)',
    price: '$249.00',
    category: 'Electronics',
    checkoutUrl: 'https://amazon.com/dp/B0BDHWDR12',
    description: 'Wireless earbuds with active noise cancellation'
  },
  {
    rank: 2,
    name: 'Lululemon Everywhere Belt Bag',
    price: '$38.00',
    category: 'Fashion',
    checkoutUrl: 'https://amazon.com/dp/B09XQJQZQM',
    description: 'Versatile crossbody bag perfect for any occasion'
  },
  {
    rank: 3,
    name: 'Stanley Adventure Quencher Tumbler 40oz',
    price: '$45.00',
    category: 'Home & Kitchen',
    checkoutUrl: 'https://amazon.com/dp/B0B3QVWZPX',
    description: 'Insulated stainless steel tumbler with handle'
  },
  {
    rank: 4,
    name: 'Rare Beauty Soft Pinch Liquid Blush',
    price: '$23.00',
    category: 'Beauty',
    checkoutUrl: 'https://amazon.com/dp/B08FXQZQM',
    description: 'Weightless liquid blush for a natural flush'
  },
  {
    rank: 5,
    name: 'UGG Scuffette II Slippers',
    price: '$90.00',
    category: 'Shoes',
    checkoutUrl: 'https://amazon.com/dp/B01MXQZQM',
    description: 'Cozy sheepskin slippers for ultimate comfort'
  },
  {
    rank: 6,
    name: 'Glossier Cloud Paint Gel Blush',
    price: '$20.00',
    category: 'Beauty',
    checkoutUrl: 'https://amazon.com/dp/B08GXQZQM',
    description: 'Gel-cream blush for a dewy, natural look'
  },
  {
    rank: 7,
    name: 'Anthropologie Monogram Mug',
    price: '$16.00',
    category: 'Home & Kitchen',
    checkoutUrl: 'https://amazon.com/dp/B09HXQZQM',
    description: 'Personalized ceramic mug with gold lettering'
  },
  {
    rank: 8,
    name: 'Fenty Beauty Gloss Bomb Universal Lip Luminizer',
    price: '$21.00',
    category: 'Beauty',
    checkoutUrl: 'https://amazon.com/dp/B07KXQZQM',
    description: 'Universal lip gloss that flatters all skin tones'
  },
  {
    rank: 9,
    name: 'Laneige Lip Sleeping Mask',
    price: '$24.00',
    category: 'Beauty',
    checkoutUrl: 'https://amazon.com/dp/B074XQZQM',
    description: 'Overnight lip treatment for soft, smooth lips'
  },
  {
    rank: 10,
    name: 'Gua Sha Facial Tool - Rose Quartz',
    price: '$12.95',
    category: 'Beauty',
    checkoutUrl: 'https://amazon.com/dp/B08MXQZQM',
    description: 'Natural rose quartz stone for facial massage'
  },
  {
    rank: 11,
    name: 'Silk Pillowcase Set',
    price: '$29.99',
    category: 'Home & Kitchen',
    checkoutUrl: 'https://amazon.com/dp/B09NXQZQM',
    description: 'Mulberry silk pillowcases for hair and skin'
  },
  {
    rank: 12,
    name: 'Bath & Body Works 3-Wick Candle',
    price: '$26.50',
    category: 'Home & Kitchen',
    checkoutUrl: 'https://amazon.com/dp/B08PXQZQM',
    description: 'Long-lasting scented candle in various fragrances'
  }
];

export default function Home() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [isSharedView, setIsSharedView] = useState(false);
  const [sharedWishlistData, setSharedWishlistData] = useState<any>(null);

  // State declarations - moved before useEffect hooks
  const [items, setItems] = useState<Item[]>([]);
  const [deletedItems, setDeletedItems] = useState<Item[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [showShoppingGuide, setShowShoppingGuide] = useState(false);
  const [showDeletedItems, setShowDeletedItems] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [editingItem, setEditingItem] = useState<Item | null>(null);
  const [newItem, setNewItem] = useState<Partial<Item>>({
    title: '',
    description: '',
    category: 'Electronics',
    image: '',
    tags: [],
    notes: '',
    price: '',
    priority: 'medium',
    checkoutUrl: ''
  });

  const [amazonGifts, setAmazonGifts] = useState(amazonMostGiftedData);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  // Check authentication and shared wishlist on component mount
  useEffect(() => {
    // Check if this is a shared wishlist view
    const urlParams = new URLSearchParams(window.location.search);
    const sharedData = urlParams.get('shared');
    
    if (sharedData) {
      try {
        const decodedData = JSON.parse(atob(sharedData));
        setIsSharedView(true);
        setSharedWishlistData(decodedData);
        setItems(decodedData.items.map((item: any, index: number) => ({
          ...item,
          id: `shared-${index}`,
          dateAdded: new Date().toISOString().split('T')[0],
          image: '',
          isFavorite: false,
          notes: '',
          rating: Math.round((Math.random() * 2 + 3) * 10) / 10,
          reviewCount: Math.floor(Math.random() * 2000) + 50,
          purchased: false
        })));
        return;
      } catch (error) {
        console.error('Invalid shared wishlist data:', error);
      }
    }

    // Normal authentication flow for non-shared views
    const user = JSON.parse(
      localStorage.getItem('santa-helper-user') ||
        sessionStorage.getItem('santa-helper-user') ||
        '{}'
    );

    if (!user.id) {
      navigate('/login');
      return;
    }

    setCurrentUser(user);

    // Load user-specific data
    const users = JSON.parse(localStorage.getItem('santa-helper-users') || '[]');
    const userData = users.find((u: any) => u.id === user.id);

    if (userData) {
      setItems(userData.wishlistItems || []);
    }
  }, [navigate]);

  // Save user data whenever items changes
  useEffect(() => {
    if (currentUser?.id) {
      const users = JSON.parse(localStorage.getItem('santa-helper-users') || '[]');
      const updatedUsers = users.map((user: any) => {
        if (user.id === currentUser.id) {
          return {
            ...user,
            wishlistItems: items
          };
        }
        return user;
      });

      localStorage.setItem('santa-helper-users', JSON.stringify(updatedUsers));
    }
  }, [items, currentUser]);

  // Auto-save items whenever they change
  useEffect(() => {
    localStorage.setItem('valeria-christmas-wishlist', JSON.stringify(items));
  }, [items]);

  // Auto-save deleted items whenever they change
  useEffect(() => {
    localStorage.setItem('valeria-deleted-items', JSON.stringify(deletedItems));
  }, [deletedItems]);

  // Fetch Amazon Most Gifted data every 10 minutes
  useEffect(() => {
    const fetchAmazonData = async () => {
      try {
        // In a real implementation, this would fetch from Amazon's API
        // For now, we'll simulate an update with the existing data
        console.log('Updating Amazon Most Gifted data...');
        setLastUpdated(new Date());
        // You would replace this with actual API call to Amazon
        // const response = await fetch('https://www.amazon.com/gp/most-gifted');
        // const data = await response.json();
        // setAmazonGifts(data);
      } catch (error) {
        console.error('Failed to update Amazon data:', error);
      }
    };

    // Initial fetch
    fetchAmazonData();

    // Set up interval to fetch every 10 minutes (600,000 ms)
    const interval = setInterval(fetchAmazonData, 600000);

    // Cleanup interval on component unmount
    return () => clearInterval(interval);
  }, []);

  const filteredItems = items.filter(item => {
    const matchesCategory =
      selectedCategory === 'All' || item.category === selectedCategory;
    const matchesSearch =
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch && !item.purchased;
  });

  const favoriteItems = items.filter(item => item.isFavorite && !item.purchased);
  const highPriorityItems = items.filter(
    item => item.priority === 'high' && !item.purchased
  );
  const purchasedItems = items.filter(item => item.purchased);

  const supportedWebsites = [
    {
      name: 'Amazon',
      domain: 'amazon.com',
      url: 'https://amazon.com',
      color: 'orange',
      icon: 'ri-amazon-line',
      description: 'Electronics, books, home goods, and everything else'
    },
    {
      name: 'Anthropologie',
      domain: 'anthropologie.com',
      url: 'https://anthropologie.com',
      color: 'green',
      icon: 'ri-leaf-line',
      description: 'Bohemian clothing, home decor, and unique gifts'
    },
    {
      name: 'Bath & Body Works',
      domain: 'bathandbodyworks.com',
      url: 'https://bathandbodyworks.com',
      color: 'purple',
      icon: 'ri-flask-line',
      description: 'Fragrances, body care, and candles'
    },
    {
      name: 'Best Buy',
      domain: 'bestbuy.com',
      url: 'https://bestbuy.com',
      color: 'blue',
      icon: 'ri-computer-line',
      description: 'Electronics, appliances, and tech accessories'
    },
    {
      name: 'Free People',
      domain: 'freepeople.com',
      url: 'https://freepeople.com',
      color: 'orange',
      icon: 'ri-flower-line',
      description: 'Boho-chic clothing and accessories'
    },
    {
      name: 'H&M',
      domain: 'hm.com',
      url: 'https://hm.com',
      color: 'red',
      icon: 'ri-t-shirt-line',
      description: 'Trendy fashion for all ages'
    },
    {
      name: 'Lululemon',
      domain: 'lululemon.com',
      url: 'https://lululemon.com',
      color: 'red',
      icon: 'ri-run-line',
      description: 'Athletic wear and yoga apparel'
    },
    {
      name: 'Nordstrom',
      domain: 'nordstrom.com',
      url: 'https://nordstrom.com',
      color: 'blue',
      icon: 'ri-shirt-line',
      description: 'Designer fashion and luxury goods'
    },
    {
      name: 'Sephora',
      domain: 'sephora.com',
      url: 'https://sephora.com',
      color: 'black',
      icon: 'ri-heart-line',
      description: 'Beauty products and cosmetics'
    },
    {
      name: 'Target',
      domain: 'target.com',
      url: 'https://target.com',
      color: 'red',
      icon: 'ri-focus-3-line',
      description: 'Affordable fashion, home, and essentials'
    },
    {
      name: 'Ulta Beauty',
      domain: 'ulta.com',
      url: 'https://ulta.com',
      color: 'pink',
      icon: 'ri-makeup-line',
      description: 'Beauty products and salon services'
    },
    {
      name: "Victoria's Secret",
      domain: 'victoriassecret.com',
      url: 'https://victoriassecret.com',
      color: 'pink',
      icon: 'ri-heart-2-line',
      description: 'Lingerie, sleepwear, and beauty'
    },
    {
      name: 'Walmart',
      domain: 'walmart.com',
      url: 'https://walmart.com',
      color: 'blue',
      icon: 'ri-shopping-cart-line',
      description: 'Everyday low prices on everything'
    },
    {
      name: 'Zara',
      domain: 'zara.com',
      url: 'https://zara.com',
      color: 'gray',
      icon: 'ri-handbag-line',
      description: 'Fast fashion and trendy styles'
    }
  ];

  const websiteItems = items.filter(item => item.websiteSource || item.amazonUrl);

  const handleAddItem = () => {
    if (newItem.title && newItem.description && newItem.price && newItem.checkoutUrl) {
      const item: Item = {
        id: Date.now().toString(),
        title: newItem.title,
        description: newItem.description,
        category: newItem.category || 'Electronics',
        dateAdded: new Date().toISOString().split('T')[0],
        image: '',
        tags: newItem.tags || [],
        isFavorite: false,
        notes: newItem.notes || '',
        price: newItem.price || '',
        priority: newItem.priority || 'medium',
        rating: Math.round((Math.random() * 2 + 3) * 10) / 10,
        reviewCount: Math.floor(Math.random() * 2000) + 50,
        websiteSource: newItem.websiteSource,
        checkoutUrl: newItem.checkoutUrl
      };
      setItems([...items, item]);
      setNewItem({
        title: '',
        description: '',
        category: 'Electronics',
        image: '',
        tags: [],
        notes: '',
        price: '',
        priority: 'medium',
        checkoutUrl: ''
      });
      setShowAddForm(false);
    }
  };

  const formatPriceInput = (value: string) => {
    // Remove all non-numeric characters except decimal point
    let numericValue = value.replace(/[^0-9.]/g, '');

    // Ensure only one decimal point
    const parts = numericValue.split('.');
    if (parts.length > 2) {
      numericValue = parts[0] + '.' + parts.slice(1).join('');
    }

    // Limit to 2 decimal places
    if (parts[1] && parts[1].length > 2) {
      numericValue = parts[0] + '.' + parts[1].substring(0, 2);
    }

    // Add dollar sign if there's a value
    if (numericValue) {
      return '$' + numericValue;
    }

    return '';
  };

  const handlePriceChange = (value: string) => {
    const formattedPrice = formatPriceInput(value);
    setNewItem({ ...newItem, price: formattedPrice });
  };

  const handleEditItem = (item: Item) => {
    setEditingItem(item);
    setNewItem(item);
    setShowAddForm(true);
  };

  const handleUpdateItem = () => {
    if (editingItem && newItem.title && newItem.description && newItem.price && newItem.checkoutUrl) {
      const updatedItems = items.map(item =>
        item.id === editingItem.id ? { ...item, ...newItem, tags: newItem.tags || [] } : item
      );
      setItems(updatedItems);
      setEditingItem(null);
      setNewItem({
        title: '',
        description: '',
        category: 'Electronics',
        image: '',
        tags: [],
        notes: '',
        price: '',
        priority: 'medium',
        checkoutUrl: ''
      });
      setShowAddForm(false);
    }
  };

  const toggleFavorite = (id: string) => {
    setItems(
      items.map(item => (item.id === id ? { ...item, isFavorite: !item.isFavorite } : item))
    );
  };

  const markAsPurchased = (id: string) => {
    setItems(
      items.map(item =>
        item.id === id ? { ...item, purchased: true, purchasedAt: new Date().toISOString() } : item
      )
    );

    // Simple Santa animation
    const santaDiv = document.createElement('div');
    santaDiv.className =
      'fixed inset-0 pointer-events-none z-50 flex items-center justify-center';
    santaDiv.innerHTML = `
      <div className="relative">
        <!-- Santa Animation -->
        <div className="santa-animation">
          <div className="flex items-center">
            <div className="text-8xl animate-pulse">🎅</div>
          </div>
        </div>
        
        <!-- Gift Purchased Message -->
        <div className="absolute -bottom-20 left-1/2 transform -translate-x-1/2 bg-green-600 text-white px-8 py-4 rounded-lg shadow-2xl">
          <div className="flex items-center gap-3 text-xl font-bold">
            <span>Gift Purchased Successfully!</span>
          </div>
        </div>
      </div>
      
      <style>
        .santa-animation {
          animation: santaAppear 3s ease-in-out;
        }
        
        @keyframes santaAppear {
          0% {
            transform: scale(0) rotate(-180deg);
            opacity: 0;
          }
          50% {
            transform: scale(1.2) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: scale(1) rotate(0deg);
            opacity: 1;
          }
        }
      </style>
    `;
    document.body.appendChild(santaDiv);

    // Remove animation after 3 seconds
    setTimeout(() => {
      if (document.body.contains(santaDiv)) {
        document.body.removeChild(santaDiv);
      }
    }, 3000);

    // Show simple success notification
    setTimeout(() => {
      const successDiv = document.createElement('div');
      successDiv.className =
        'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      successDiv.innerHTML = `
        <div className="flex items-center gap-2">
          <i className="ri-check-line"></i>
          <span>Item marked as purchased! <button onclick="this.parentElement.parentElement.parentElement.remove()" className="underline ml-2">Dismiss</button></span>
        </div>
      `;
      document.body.appendChild(successDiv);
      setTimeout(() => {
        if (document.body.contains(successDiv)) {
          document.body.removeChild(successDiv);
        }
      }, 5000);
    }, 3200);
  };

  const deleteItem = (id: string) => {
    const itemToDelete = items.find(item => item.id === id);
    if (itemToDelete) {
      const deletedItem = {
        ...itemToDelete,
        deletedAt: new Date().toISOString()
      };
      setDeletedItems([...deletedItems, deletedItem]);
      setItems(items.filter(item => item.id !== id));
      const successDiv = document.createElement('div');
      successDiv.className =
        'fixed top-4 right-4 bg-orange-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      successDiv.innerHTML = `
        <div className="flex items-center gap-2">
          <i className="ri-delete-bin-line"></i>
          <span>Item moved to trash. <button onclick="this.parentElement.parentElement.parentElement.remove()" className="underline ml-2">Dismiss</button></span>
        </div>
      `;
      document.body.appendChild(successDiv);
      setTimeout(() => {
        if (document.body.contains(successDiv)) {
          document.body.removeChild(successDiv);
        }
      }, 5000);
    }
  };

  const restoreItem = (id: string) => {
    const itemToRestore = deletedItems.find(item => item.id === id);
    if (itemToRestore) {
      const { deletedAt, ...restoredItem } = itemToRestore;
      setItems([...items, restoredItem]);
      setDeletedItems(deletedItems.filter(item => item.id !== id));
      const successDiv = document.createElement('div');
      successDiv.className =
        'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      successDiv.textContent = 'Item restored to your wishlist!';
      document.body.appendChild(successDiv);
      setTimeout(() => document.body.removeChild(successDiv), 3000);
    }
  };

  const permanentlyDeleteItem = (id: string) => {
    if (
      confirm(
        'Are you sure you want to permanently delete this item? This action cannot be undone.'
      )
    ) {
      setDeletedItems(deletedItems.filter(item => item.id !== id));
      const successDiv = document.createElement('div');
      successDiv.className =
        'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      successDiv.textContent = 'Item permanently deleted!';
      document.body.appendChild(successDiv);
      setTimeout(() => document.body.removeChild(successDiv), 3000);
    }
  };

  const clearAllDeletedItems = () => {
    if (
      confirm(
        'Are you sure you want to permanently delete all items in trash? This action cannot be undone.'
      )
    ) {
      setDeletedItems([]);
      const successDiv = document.createElement('div');
      successDiv.className =
        'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      successDiv.textContent = 'All deleted items permanently removed!';
      document.body.appendChild(successDiv);
      setTimeout(() => document.body.removeChild(successDiv), 3000);
    }
  };

  const restoreAllItems = () => {
    if (deletedItems.length > 0) {
      const restoredItems = deletedItems.map(({ deletedAt, ...item }) => item);
      setItems([...items, ...restoredItems]);
      setDeletedItems([]);
      const successDiv = document.createElement('div');
      successDiv.className =
        'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      successDiv.textContent = `${restoredItems.length} items restored to your wishlist!`;
      document.body.appendChild(successDiv);
      setTimeout(() => document.body.removeChild(successDiv), 3000);
    }
  };

  const handleTagInput = (value: string) => {
    const tags = value
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag);
    setNewItem({ ...newItem, tags });
  };

  const generateShareLink = () => {
    const wishlistData = {
      user: currentUser?.firstName || 'Someone',
      items: items
        .filter(item => !item.purchased)
        .map(item => ({
          title: item.title,
          description: item.description,
          category: item.category,
          priority: item.priority,
          checkoutUrl: item.checkoutUrl,
          tags: item.tags
        }))
    };

    const encodedData = btoa(JSON.stringify(wishlistData));
    const shareUrl = `${window.location.origin}${window.location.pathname}?shared=${encodedData}`;

    return shareUrl;
  };

  const copyShareLink = async () => {
    const shareUrl = generateShareLink();
    try {
      await navigator.clipboard.writeText(shareUrl);
      const successDiv = document.createElement('div');
      successDiv.className =
        'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      successDiv.textContent = 'Share link copied to clipboard! 🎄';
      document.body.appendChild(successDiv);
      setTimeout(() => document.body.removeChild(successDiv), 3000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const shareViaEmail = () => {
    const shareUrl = generateShareLink();
    const subject = `🎄 ${currentUser?.firstName || 'Someone'}'s Christmas Wishlist`;
    const body = `Ho ho ho! 🎅

I've created my Christmas wishlist and wanted to share it with you!

View my wishlist here: ${shareUrl}

Merry Christmas! 🎁`;
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
  };

  const shareViaWhatsApp = () => {
    const shareUrl = generateShareLink();
    const message = `🎄 Ho ho ho! Check out my Christmas wishlist: ${shareUrl} 🎁`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-700';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700';
      case 'low':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'ri-fire-fill';
      case 'medium':
        return 'ri-star-fill';
      case 'low':
        return 'ri-leaf-fill';
      default:
        return 'ri-circle-fill';
    }
  };

  const getPriorityFireColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'text-red-500'; // Red fire
      case 'medium':
        return 'text-blue-500'; // Blue fire
      case 'low':
        return 'text-green-500'; // Green fire
      default:
        return 'text-gray-500';
    }
  };

  const renderStarRating = (rating: number) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <div className="flex items-center gap-1">
        {[...Array(fullStars)].map((_, i) => (
          <i key={`full-${i}`} className="ri-star-fill text-yellow-400 text-sm"></i>
        ))}
        {hasHalfStar && <i className="ri-star-half-fill text-yellow-400 text-sm"></i>}
        {[...Array(emptyStars)].map((_, i) => (
          <i key={`empty-${i}`} className="ri-star-line text-gray-300 text-sm"></i>
        ))}
      </div>
    );
  };

  const openSantasHelp = () => {
    const modal = document.createElement('div');
    modal.className = 'fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50';
    modal.innerHTML = `
      <div class="bg-white rounded-lg p-8 max-w-md w-full relative">
        <button onclick="this.closest('.fixed').remove()" class="absolute top-4 right-4 w-8 h-8 flex items-center justify-center cursor-pointer rounded-full bg-gray-200 hover:bg-gray-300">
          <i class="ri-close-line"></i>
        </button>
        <div class="text-center">
          <div class="text-6xl mb-4">🎅</div>
          <h3 class="text-2xl font-bold text-red-600 mb-4" style="font-family: 'Pacifico', serif;">Ho Ho Ho!</h3>
          <p class="text-gray-600 mb-6">Santa is here to help you with your Christmas wishlist! What would you like to know?</p>
          <div class="space-y-3">
            <button onclick="alert('🎁 Try adding items you really want for Christmas! Use high priority for your most wanted gifts.'); this.closest('.fixed').remove();" class="w-full bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors">
              🎁 Gift Ideas
            </button>
            <button onclick="alert('🎯 Use priority levels: High for must-haves, Medium for nice-to-haves, Low for future gifts!'); this.closest('.fixed').remove();" class="w-full bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
              🎯 Priority Tips
            </button>
            <button onclick="alert('📤 Use the Share button to send your wishlist to family and friends so they know what you want!'); this.closest('.fixed').remove();" class="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
              📤 Sharing Help
            </button>
          </div>
        </div>
      </div>
    `;
    document.body.appendChild(modal);
  };

  const getRankingFlameColor = (rank: number) => {
    if (rank <= 3) return 'text-red-500';
    if (rank <= 6) return 'text-orange-500';
    if (rank <= 9) return 'text-yellow-500';
    return 'text-blue-500';
  };

  const getRankingBadgeColor = (rank: number) => {
    if (rank <= 3) return 'from-red-500 to-red-600';
    if (rank <= 6) return 'from-orange-500 to-orange-600';
    if (rank <= 9) return 'from-yellow-500 to-yellow-600';
    return 'from-blue-500 to-blue-600';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-green-50 to-red-50">
      <Header />

      {/* Hero Section */}
      <section
        id="home"
        className="relative py-16 px-4 sm:px-6 lg:px-8 overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/b56bb4660306a0344565dcbd0cec6acc.jpg')`
          }}
        >
          <div className="absolute inset-0 bg-white/80"></div>
        </div>

        <div className="relative w-full">
          <div className="text-center max-w-6xl mx-auto">
            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-red-600 mb-6"
              style={{ fontFamily: '"Pacifico", serif' }}
            >
              🎅 Santa's Helper
            </h1>
            {isSharedView ? (
              <p className="text-lg text-gray-700 mb-4">
                🎄 {sharedWishlistData?.user || 'Someone'}'s Christmas Wishlist
              </p>
            ) : currentUser && (
              <p className="text-lg text-gray-700 mb-4">
                Welcome back, {currentUser.firstName}! 🎄
              </p>
            )}
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
              {isSharedView 
                ? `Browse ${sharedWishlistData?.user || 'this person'}'s Christmas wishlist and help make their holiday dreams come true!`
                : 'Your magical Christmas companion to organize and track all the gifts you\'re hoping for this holiday season'
              }
            </p>
            {!isSharedView && (
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  size="lg"
                  onClick={() => document.getElementById('gifts')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  <i className="ri-gift-line mr-2"></i>
                  View My Wishlist
                </Button>
                <Button variant="outline" size="lg" onClick={() => setShowAddForm(true)}>
                  <i className="ri-add-line mr-2"></i>
                  Add New Gift
                </Button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Action Buttons - Only show for non-shared views */}
      {!isSharedView && (
        <section className="py-8 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-wrap gap-4 justify-center">
              <Button onClick={() => setShowAddForm(true)}>
                <i className="ri-add-line mr-2"></i>
                Add New Item
              </Button>
              <Button variant="outline" onClick={() => setShowShareModal(true)}>
                <i className="ri-share-line mr-2"></i>
                Share Wishlist
              </Button>
              <Button variant="outline" onClick={() => setShowShoppingGuide(true)}>
                <i className="ri-shopping-bag-line mr-2"></i>
                Shopping Guide
              </Button>
              <Button variant="outline" onClick={() => setShowDeletedItems(true)}>
                <i className="ri-delete-bin-line mr-2"></i>
                Deleted Items ({deletedItems.length})
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Shared Wishlist Notice */}
      {isSharedView && (
        <section className="py-8 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="bg-gradient-to-r from-green-50 to-red-50 border border-green-200 rounded-lg p-6 text-center">
              <div className="flex items-center justify-center mb-4">
                <i className="ri-gift-line text-3xl text-green-600 mr-3"></i>
                <h3 className="text-xl font-semibold text-gray-800">
                  You're viewing {sharedWishlistData?.user || 'someone'}'s Christmas Wishlist
                </h3>
              </div>
              <p className="text-gray-600 mb-4">
                Help make their Christmas special by purchasing gifts from their wishlist!
              </p>
              <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                <i className="ri-information-line"></i>
                <span>Click "Buy Now" on any item to purchase it for them</span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Wishlist Items */}
      <section id="gifts" className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800">
              {isSharedView 
                ? `${sharedWishlistData?.user || 'Christmas'} Wishlist (${filteredItems.length} items)`
                : `My Christmas Wishlist (${filteredItems.length} items)`
              }
            </h2>
            {!isSharedView && (
              <div className="flex gap-2">
                <Button size="sm" onClick={() => setShowAddForm(true)}>
                  <i className="ri-add-line mr-1"></i>
                  Add Item
                </Button>
              </div>
            )}
          </div>

          {filteredItems.length === 0 ? (
            <Card className="p-12 text-center">
              <i className="ri-gift-line text-6xl text-gray-300 mb-4"></i>
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No items found</h3>
              <p className="text-gray-500 mb-6">
                {isSharedView 
                  ? 'This wishlist is currently empty.'
                  : searchTerm || selectedCategory !== 'All'
                  ? 'Try adjusting your filters or search terms'
                  : 'Start building your Christmas wishlist by adding your first item!'
                }
              </p>
              {!isSharedView && (
                <Button onClick={() => setShowAddForm(true)}>
                  <i className="ri-add-line mr-2"></i>
                  Add Your First Item
                </Button>
              )}
            </Card>
          ) : (
            <div className="space-y-8">
              {/* High Priority Tier */}
              {filteredItems.filter(item => item.priority === 'high').length > 0 && (
                <div>
                  <div className="flex items-center mb-6">
                    <div className="flex items-center bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-2 rounded-full">
                      <i className="ri-fire-fill text-red-200 mr-2 text-lg"></i>
                      <h3 className="font-bold text-lg">High Priority Gifts</h3>
                      <span className="ml-3 bg-red-400 text-red-100 px-2 py-1 rounded-full text-sm font-medium">
                        {filteredItems.filter(item => item.priority === 'high').length}
                      </span>
                    </div>
                    <div className="flex-1 h-px bg-gradient-to-r from-red-300 to-transparent ml-4"></div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-4">
                    {filteredItems
                      .filter(item => item.priority === 'high')
                      .map(item => (
                        <Card
                          key={item.id}
                          className="overflow-hidden hover:shadow-lg transition-shadow border-l-4 border-red-500"
                        >
                          <div className="p-6">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <h3 className="font-semibold text-gray-800 mb-1">{item.title}</h3>
                                <p className="text-sm text-gray-600 mb-2">{item.description}</p>
                              </div>
                              {!isSharedView && (
                                <button
                                  onClick={() => toggleFavorite(item.id)}
                                  className={`w-8 h-8 flex items-center justify-center cursor-pointer rounded-full ${
                                    item.isFavorite ? 'text-red-500' : 'text-gray-400 hover:text-red-500'
                                  }`}
                                >
                                  <i className={`ri-heart-${item.isFavorite ? 'fill' : 'line'}`}></i>
                                </button>
                              )}
                            </div>

                            <div className="flex items-center gap-2 mb-3">
                              <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                                <i className="ri-fire-fill mr-1 text-red-500"></i>
                                High Priority
                              </span>
                              <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">
                                {item.category}
                              </span>
                            </div>

                            {item.rating && (
                              <div className="flex items-center gap-2 mb-3">
                                {renderStarRating(item.rating)}
                                <span className="text-sm text-gray-500">({item.reviewCount})</span>
                              </div>
                            )}

                            {item.tags && item.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mb-3">
                                {item.tags.map((tag, index) => (
                                  <span
                                    key={index}
                                    className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}

                            <div className="flex gap-2 mt-4">
                              {item.checkoutUrl && (
                                <Button
                                  size="sm"
                                  onClick={() => window.open(item.checkoutUrl, '_blank')}
                                  className="flex-1"
                                >
                                  <i className="ri-shopping-cart-line mr-1"></i>
                                  Buy Now
                                </Button>
                              )}
                              {!isSharedView && (
                                <>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => markAsPurchased(item.id)}
                                    className="text-green-600 border-green-600 hover:bg-green-50"
                                  >
                                    <i className="ri-gift-line"></i>
                                  </Button>
                                  <Button variant="outline" size="sm" onClick={() => handleEditItem(item)}>
                                    <i className="ri-edit-line"></i>
                                  </Button>
                                  <Button variant="outline" size="sm" onClick={() => deleteItem(item.id)}>
                                    <i className="ri-delete-bin-line"></i>
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        </Card>
                      ))}
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-red-600 font-medium">
                      🎯 {isSharedView ? 'Most wanted Christmas gifts!' : 'These are your most wanted Christmas gifts!'}
                    </p>
                  </div>
                </div>
              )}

              {/* Medium Priority Tier */}
              {filteredItems.filter(item => item.priority === 'medium').length > 0 && (
                <div>
                  <div className="flex items-center mb-6">
                    <div className="flex items-center bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-2 rounded-full">
                      <i className="ri-fire-fill text-blue-200 mr-2 text-lg"></i>
                      <h3 className="font-bold text-lg">Medium Priority Gifts</h3>
                      <span className="ml-3 bg-blue-400 text-blue-100 px-2 py-1 rounded-full text-sm font-medium">
                        {filteredItems.filter(item => item.priority === 'medium').length}
                      </span>
                    </div>
                    <div className="flex-1 h-px bg-gradient-to-r from-blue-300 to-transparent ml-4"></div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-4">
                    {filteredItems
                      .filter(item => item.priority === 'medium')
                      .map(item => (
                        <Card
                          key={item.id}
                          className="overflow-hidden hover:shadow-lg transition-shadow border-l-4 border-blue-500"
                        >
                          <div className="p-6">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <h3 className="font-semibold text-gray-800 mb-1">{item.title}</h3>
                                <p className="text-sm text-gray-600 mb-2">{item.description}</p>
                              </div>
                              {!isSharedView && (
                                <button
                                  onClick={() => toggleFavorite(item.id)}
                                  className={`w-8 h-8 flex items-center justify-center cursor-pointer rounded-full ${
                                    item.isFavorite ? 'text-red-500' : 'text-gray-400 hover:text-red-500'
                                  }`}
                                >
                                  <i className={`ri-heart-${item.isFavorite ? 'fill' : 'line'}`}></i>
                                </button>
                              )}
                            </div>

                            <div className="flex items-center gap-2 mb-3">
                              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                                <i className="ri-fire-fill mr-1 text-blue-500"></i>
                                Medium Priority
                              </span>
                              <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">
                                {item.category}
                              </span>
                            </div>

                            {item.rating && (
                              <div className="flex items-center gap-2 mb-3">
                                {renderStarRating(item.rating)}
                                <span className="text-sm text-gray-500">({item.reviewCount})</span>
                              </div>
                            )}

                            {item.tags && item.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mb-3">
                                {item.tags.map((tag, index) => (
                                  <span
                                    key={index}
                                    className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}

                            <div className="flex gap-2 mt-4">
                              {item.checkoutUrl && (
                                <Button
                                  size="sm"
                                  onClick={() => window.open(item.checkoutUrl, '_blank')}
                                  className="flex-1"
                                >
                                  <i className="ri-shopping-cart-line mr-1"></i>
                                  Buy Now
                                </Button>
                              )}
                              {!isSharedView && (
                                <>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => markAsPurchased(item.id)}
                                    className="text-green-600 border-green-600 hover:bg-green-50"
                                  >
                                    <i className="ri-gift-line"></i>
                                  </Button>
                                  <Button variant="outline" size="sm" onClick={() => handleEditItem(item)}>
                                    <i className="ri-edit-line"></i>
                                  </Button>
                                  <Button variant="outline" size="sm" onClick={() => deleteItem(item.id)}>
                                    <i className="ri-delete-bin-line"></i>
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        </Card>
                      ))}
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-blue-600 font-medium">
                      ⭐ {isSharedView ? 'Nice-to-have gifts!' : 'Nice-to-have gifts that would make you happy!'}
                    </p>
                  </div>
                </div>
              )}

              {/* Low Priority Tier */}
              {filteredItems.filter(item => item.priority === 'low').length > 0 && (
                <div>
                  <div className="flex items-center mb-6">
                    <div className="flex items-center bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-2 rounded-full">
                      <i className="ri-fire-fill text-green-200 mr-2 text-lg"></i>
                      <h3 className="font-bold text-lg">Low Priority Gifts</h3>
                      <span className="ml-3 bg-green-400 text-green-100 px-2 py-1 rounded-full text-sm font-medium">
                        {filteredItems.filter(item => item.priority === 'low').length}
                      </span>
                    </div>
                    <div className="flex-1 h-px bg-gradient-to-r from-green-300 to-transparent ml-4"></div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-4">
                    {filteredItems
                      .filter(item => item.priority === 'low')
                      .map(item => (
                        <Card
                          key={item.id}
                          className="overflow-hidden hover:shadow-lg transition-shadow border-l-4 border-green-500"
                        >
                          <div className="p-6">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <h3 className="font-semibold text-gray-800 mb-1">{item.title}</h3>
                                <p className="text-sm text-gray-600 mb-2">{item.description}</p>
                              </div>
                              {!isSharedView && (
                                <button
                                  onClick={() => toggleFavorite(item.id)}
                                  className={`w-8 h-8 flex items-center justify-center cursor-pointer rounded-full ${
                                    item.isFavorite ? 'text-red-500' : 'text-gray-400 hover:text-red-500'
                                  }`}
                                >
                                  <i className={`ri-heart-${item.isFavorite ? 'fill' : 'line'}`}></i>
                                </button>
                              )}
                            </div>

                            <div className="flex items-center gap-2 mb-3">
                              <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                                <i className="ri-fire-fill mr-1 text-green-500"></i>
                                Low Priority
                              </span>
                              <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">
                                {item.category}
                              </span>
                            </div>

                            {item.rating && (
                              <div className="flex items-center gap-2 mb-3">
                                {renderStarRating(item.rating)}
                                <span className="text-sm text-gray-500">({item.reviewCount})</span>
                              </div>
                            )}

                            {item.tags && item.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mb-3">
                                {item.tags.map((tag, index) => (
                                  <span
                                    key={index}
                                    className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}

                            <div className="flex gap-2 mt-4">
                              {item.checkoutUrl && (
                                <Button
                                  size="sm"
                                  onClick={() => window.open(item.checkoutUrl, '_blank')}
                                  className="flex-1"
                                >
                                  <i className="ri-shopping-cart-line mr-1"></i>
                                  Buy Now
                                </Button>
                              )}
                              {!isSharedView && (
                                <>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => markAsPurchased(item.id)}
                                    className="text-green-600 border-green-600 hover:bg-green-50"
                                  >
                                    <i className="ri-gift-line"></i>
                                  </Button>
                                  <Button variant="outline" size="sm" onClick={() => handleEditItem(item)}>
                                    <i className="ri-edit-line"></i>
                                  </Button>
                                  <Button variant="outline" size="sm" onClick={() => deleteItem(item.id)}>
                                    <i className="ri-delete-bin-line"></i>
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        </Card>
                      ))}
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-green-600 font-medium">
                      🌿 {isSharedView ? 'Perfect for stocking stuffers!' : 'Perfect for stocking stuffers and future occasions!'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Gifts Purchased Section - Only show for non-shared views */}
      {!isSharedView && purchasedItems.length > 0 && (
        <section className="py-8 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-yellow-50 to-amber-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center mb-6">
              <div className="flex items-center bg-gradient-to-r from-yellow-600 to-amber-600 text-white px-4 py-2 rounded-full">
                <i className="ri-gift-fill text-yellow-200 mr-2 text-lg"></i>
                <h3 className="font-bold text-lg">🎁 Gifts Purchased</h3>
                <span className="ml-3 bg-yellow-500 text-yellow-100 px-2 py-1 rounded-full text-sm font-medium">
                  {purchasedItems.length}
                </span>
              </div>
              <div className="flex-1 h-px bg-gradient-to-r from-yellow-300 to-transparent ml-4"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {purchasedItems.map(item => (
                <Card
                  key={item.id}
                  className="overflow-hidden bg-gradient-to-br from-yellow-50 to-white border-l-4 border-yellow-500 relative"
                >
                  {/* Christmas Badge */}
                  <div className="absolute top-2 right-2 bg-yellow-600 text-white px-2 py-1 rounded-full text-xs font-bold">
                    🎅 PURCHASED
                  </div>

                  <div className="p-4">
                    <div className="mb-3">
                      <h4 className="font-semibold text-gray-800 text-sm mb-1 line-clamp-2">
                        {item.title}
                      </h4>
                      <p className="text-xs text-gray-600 line-clamp-2">{item.description}</p>
                    </div>

                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
                        <i className="ri-check-fill mr-1"></i>
                        Purchased
                      </span>
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                        {item.category}
                      </span>
                    </div>

                    {item.purchasedAt && (
                      <div className="text-xs text-gray-500 mb-2">
                        <i className="ri-calendar-line mr-1"></i>
                        {new Date(item.purchasedAt).toLocaleDateString()}
                      </div>
                    )}

                    {item.tags && item.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {item.tags.slice(0, 2).map((tag, index) => (
                          <span key={index} className="px-2 py-1 bg-blue-100 text-blue-600 rounded text-xs">
                            {tag}
                          </span>
                        ))}
                        {item.tags.length > 2 && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-500 rounded text-xs">
                            +{item.tags.length - 2}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Christmas Decoration */}
                    <div className="absolute bottom-2 right-2 text-yellow-500 opacity-20">
                      <i className="ri-gift-2-fill text-lg"></i>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            <div className="text-center mt-6">
              <p className="text-sm text-yellow-600 font-medium">
                🎄 Ho ho ho! {purchasedItems.length} gift{purchasedItems.length !== 1 ? 's' : ''} purchased! Santa is proud! 🎅
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Amazon Most Gifted Section - Only show for non-shared views */}
      {!isSharedView && (
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-orange-50 to-red-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">🎁 Amazon's Most Gifted</h2>
              <p className="text-gray-600 mb-2">Trending gifts that are flying off the shelves</p>
              <p className="text-sm text-gray-500">
                Last updated: {lastUpdated.toLocaleTimeString()}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {amazonGifts.map(gift => (
                <Card
                  key={gift.rank}
                  onClick={() => window.open(gift.checkoutUrl, '_blank')}
                  className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer relative"
                >
                  {/* Ranking Badge */}
                  <div
                    className={`absolute top-3 left-3 w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-r ${getRankingBadgeColor(
                      gift.rank
                    )} text-white text-sm font-bold z-10`}
                  >
                    {gift.rank}
                  </div>

                  {/* Flame Icon */}
                  <div className="absolute top-2 right-3 z-10">
                    <i className={`ri-fire-fill text-xl ${getRankingFlameColor(gift.rank)}`}></i>
                  </div>

                  <div className="p-6 pt-12">
                    <h3 className="font-semibold text-gray-800 mb-2 line-clamp-2">{gift.name}</h3>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{gift.description}</p>

                    <div className="flex items-center justify-between mb-3">
                      <span className="text-lg font-bold text-green-600">{gift.price}</span>
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">{gift.category}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-blue-600 font-medium">Buy Now</span>
                      <i className="ri-external-link-line text-blue-600"></i>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Color Legend */}
            <div className="mt-8 text-center">
              <div className="inline-flex items-center gap-6 bg-white rounded-lg px-6 py-3 shadow-sm">
                <span className="text-sm font-medium text-gray-700">Ranking:</span>
                <div className="flex items-center gap-1">
                  <i className="ri-fire-fill text-red-500"></i>
                  <span className="text-xs text-gray-600">Top 3</span>
                </div>
                <div className="flex items-center gap-1">
                  <i className="ri-fire-fill text-orange-500"></i>
                  <span className="text-xs text-gray-600">4-6</span>
                </div>
                <div className="flex items-center gap-1">
                  <i className="ri-fire-fill text-yellow-500"></i>
                  <span className="text-xs text-gray-600">7-9</span>
                </div>
                <div className="flex items-center gap-1">
                  <i className="ri-fire-fill text-blue-500"></i>
                  <span className="text-xs text-gray-600">10+</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Modals - Only show for non-shared views */}
      {!isSharedView && (
        <>
          {/* Share Wishlist Modal */}
          {showShareModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
              <Card className="w-full max-w-md">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-semibold text-gray-800">🎄 Share Your Wishlist</h3>
                    <button
                      onClick={() => setShowShareModal(false)}
                      className="w-8 h-8 flex items-center justify-center cursor-pointer rounded-full bg-gray-200 hover:bg-gray-300"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  </div>

                  <div className="space-y-4">
                    <p className="text-gray-600 text-center mb-6">
                      Share your Christmas wishlist with family and friends so they know exactly what you're hoping for! 🎁
                    </p>

                    <Button onClick={copyShareLink} className="w-full">
                      <i className="ri-link mr-2"></i>
                      Copy Share Link
                    </Button>

                    <Button variant="outline" onClick={shareViaEmail} className="w-full">
                      <i className="ri-mail-line mr-2"></i>
                      Share via Email
                    </Button>

                    <Button variant="outline" onClick={shareViaWhatsApp} className="w-full">
                      <i className="ri-whatsapp-line mr-2"></i>
                      Share via WhatsApp
                    </Button>

                    <div className="mt-6 p-4 bg-red-50 rounded-lg border border-red-200">
                      <div className="flex items-start">
                        <i className="ri-information-line text-red-500 mr-2 mt-1"></i>
                        <div>
                          <p className="text-sm text-red-700 font-medium">🎅 Santa's Tip:</p>
                          <p className="text-sm text-red-600">
                            When someone purchases a gift from your wishlist, it will automatically be marked as purchased and removed from the list!
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Shopping Guide Modal */}
          {showShoppingGuide && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
              <Card className="w-full max-w-7xl max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-semibold text-gray-800">🛍️ Christmas Shopping Guide</h3>
                    <button
                      onClick={() => setShowShoppingGuide(false)}
                      className="w-8 h-8 flex items-center justify-center cursor-pointer rounded-full bg-gray-200 hover:bg-gray-300"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Main Content */}
                    <div className="lg:col-span-2">
                      {/* Priority Tiers Explanation */}
                      <div className="mb-8">
                        <h4 className="text-xl font-semibold text-gray-800 mb-4">🔥 Priority Tiers Explained</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <Card className="p-4 border-l-4 border-red-500">
                            <div className="flex items-center mb-2">
                              <i className="ri-fire-fill text-red-500 text-xl mr-2"></i>
                              <h5 className="font-semibold text-red-700">High Priority</h5>
                            </div>
                            <p className="text-sm text-gray-600">
                              Your most wanted Christmas gifts. These are the items you're really hoping to receive and would make your holiday extra
                              special.
                            </p>
                          </Card>
                          <Card className="p-4 border-l-4 border-blue-500">
                            <div className="flex items-center mb-2">
                              <i className="ri-fire-fill text-blue-500 text-xl mr-2"></i>
                              <h5 className="font-semibold text-blue-700">Medium Priority</h5>
                            </div>
                            <p className="text-sm text-gray-600">
                              Nice-to-have items that would make great gifts but aren't essential. Good backup options for gift-givers.
                            </p>
                          </Card>
                          <Card className="p-4 border-l-4 border-green-500">
                            <div className="flex items-center mb-2">
                              <i className="ri-fire-fill text-green-500 text-xl mr-2"></i>
                              <h5 className="font-semibold text-green-700">Low Priority</h5>
                            </div>
                            <p className="text-sm text-gray-600">
                              Items you might like but can wait for future occasions. Perfect for stocking stuffers or smaller gifts.
                            </p>
                          </Card>
                        </div>
                      </div>

                      {/* Santa's Help */}
                      <div className="mb-8">
                        <h4 className="text-xl font-semibold text-gray-800 mb-4">🎅 Santa's Help</h4>
                        <Card className="p-6 bg-gradient-to-r from-red-50 to-green-50">
                          <div className="flex items-center mb-4">
                            <div className="text-3xl mr-3">🎅</div>
                            <div>
                              <h5 className="font-semibold text-gray-800" style={{ fontFamily: '"Pacifico", serif' }}>
                                Get Santa's Gift Recommendations
                              </h5>
                              <p className="text-sm text-gray-600">
                                Chat with Santa to discover perfect gifts based on your interests and budget
                              </p>
                            </div>
                          </div>
                          <Button
                            onClick={openSantasHelp}
                            className="bg-red-600 hover:bg-red-700 text-white font-bold"
                            style={{ fontFamily: '"Pacifico", serif' }}
                          >
                            🎅 Talk to Santa
                          </Button>
                        </Card>
                      </div>

                      {/* Supported Websites */}
                      <div>
                        <h4 className="text-xl font-semibold text-gray-800 mb-4">🌐 Supported Shopping Websites</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {supportedWebsites.map(website => (
                            <Card key={website.name} className="p-4 hover:shadow-md transition-shadow">
                              <div className="flex items-center mb-2">
                                <div className={`w-8 h-8 flex items-center justify-center rounded-full bg-${website.color}-100 mr-3`}>
                                  <i className={`${website.icon} text-${website.color}-600`}></i>
                                </div>
                                <div className="flex-1">
                                  <h5 className="font-semibold text-gray-800">{website.name}</h5>
                                  <p className="text-xs text-gray-500">{website.domain}</p>
                                </div>
                                <a
                                  href={website.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800"
                                >
                                  <i className="ri-external-link-line"></i>
                                </a>
                              </div>
                              <p className="text-sm text-gray-600">{website.description}</p>
                            </Card>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Amazon Most Gifted Sidebar */}
                    <div className="lg:col-span-1">
                      <div className="sticky top-4">
                        <h4 className="text-lg font-semibold text-gray-800 mb-4">🎁 Most Purchased Gifts</h4>
                        <Card className="p-4 mb-4">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-medium text-gray-700">Amazon's Most Gifted</span>
                            <a
                              href="https://www.amazon.com/gp/most-gifted"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:text-blue-800"
                            >
                              View Full List
                            </a>
                          </div>
                          <p className="text-xs text-gray-500 mb-3">
                            Last updated: {lastUpdated.toLocaleTimeString()}
                          </p>
                          <div className="space-y-3 max-h-96 overflow-y-auto">
                            {amazonGifts.map(gift => (
                              <div
                                key={gift.rank}
                                onClick={() => window.open(gift.checkoutUrl, '_blank')}
                                className="p-3 bg-white rounded-lg border hover:shadow-md transition-shadow cursor-pointer"
                              >
                                <div className="flex items-start justify-between mb-2">
                                  <div className="flex-1">
                                    <div className="flex items-center mb-1">
                                      <div
                                        className={`w-6 h-6 flex items-center justify-center rounded-full bg-gradient-to-r ${getRankingBadgeColor(
                                          gift.rank
                                        )} text-white text-xs font-bold mr-2 relative`}
                                      >
                                        {gift.rank}
                                        <i
                                          className={`ri-fire-fill absolute -top-1 -right-1 text-xs ${getRankingFlameColor(
                                            gift.rank
                                          )}`}
                                        ></i>
                                      </div>
                                      <h6 className="font-medium text-sm text-gray-800 line-clamp-2">{gift.name}</h6>
                                    </div>
                                    <p className="text-xs text-gray-600 mb-1">{gift.description}</p>
                                    <div className="flex items-center justify-between">
                                      <span className="text-sm font-bold text-green-600">{gift.price}</span>
                                      <span className="text-xs text-gray-500">{gift.category}</span>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-blue-600">Buy Now</span>
                                  <i className="ri-shopping-cart-line text-blue-600 text-sm"></i>
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* Color Legend */}
                          <div className="mt-4 pt-3 border-t border-gray-200">
                            <p className="text-xs font-medium text-gray-700 mb-2">Ranking Colors:</p>
                            <div className="grid grid-cols-2 gap-1 text-xs">
                              <div className="flex items-center">
                                <i className="ri-fire-fill text-red-500 mr-1"></i>
                                <span className="text-gray-600">Top 3</span>
                              </div>
                              <div className="flex items-center">
                                <i className="ri-fire-fill text-orange-500 mr-1"></i>
                                <span className="text-gray-600">4-6</span>
                              </div>
                              <div className="flex items-center">
                                <i className="ri-fire-fill text-yellow-500 mr-1"></i>
                                <span className="text-gray-600">7-9</span>
                              </div>
                              <div className="flex items-center">
                                <i className="ri-fire-fill text-blue-500 mr-1"></i>
                                <span className="text-gray-600">10+</span>
                              </div>
                            </div>
                          </div>
                        </Card>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Deleted Items Modal */}
          {showDeletedItems && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
              <Card className="w-full max-w-6xl max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-semibold text-gray-800 flex items-center">
                      <i className="ri-delete-bin-line text-red-600 mr-3"></i>
                      Deleted Items ({deletedItems.length})
                    </h3>
                    <div className="flex items-center gap-3">
                      {deletedItems.length > 0 && (
                        <>
                          <Button size="sm" onClick={restoreAllItems}>
                            <i className="ri-refresh-line mr-2"></i>
                            Restore All
                          </Button>
                          <Button variant="outline" size="sm" onClick={clearAllDeletedItems}>
                            <i className="ri-delete-bin-2-line mr-2"></i>
                            Empty Trash
                          </Button>
                        </>
                      )}
                      <button
                        onClick={() => setShowDeletedItems(false)}
                        className="w-8 h-8 flex items-center justify-center cursor-pointer rounded-full bg-gray-200 hover:bg-gray-300"
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    </div>
                  </div>

                  {deletedItems.length === 0 ? (
                    <div className="text-center py-12">
                      <i className="ri-delete-bin-line text-6xl text-gray-300 mb-4"></i>
                      <h4 className="text-xl font-semibold text-gray-600 mb-2">Trash is Empty</h4>
                      <p className="text-gray-500">
                        Deleted items will appear here and can be restored or permanently deleted.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {deletedItems.map(item => (
                        <Card key={item.id} className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-800">{item.title}</h4>
                              <p className="text-sm text-gray-600 mb-1">{item.description}</p>
                              <div className="flex items-center gap-4 text-sm text-gray-500">
                                <span>Category: {item.category}</span>
                                <span>Price: {item.price}</span>
                                <span>Deleted: {new Date(item.deletedAt!).toLocaleDateString()}</span>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" onClick={() => restoreItem(item.id)}>
                                <i className="ri-arrow-go-back-line mr-1"></i>
                                Restore
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => permanentlyDeleteItem(item.id)}>
                                <i className="ri-delete-bin-6-line mr-1"></i>
                                Delete Forever
                              </Button>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              </Card>
            </div>
          )}

          {/* Add/Edit Item Modal */}
          {showAddForm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
              <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-semibold text-gray-800">
                      {editingItem ? 'Edit Item' : 'Add New Item'}
                    </h3>
                    <button
                      onClick={() => {
                        setShowAddForm(false);
                        setEditingItem(null);
                        setNewItem({
                          title: '',
                          description: '',
                          category: 'Electronics',
                          image: '',
                          tags: [],
                          notes: '',
                          price: '',
                          priority: 'medium',
                          checkoutUrl: ''
                        });
                      }}
                      className="w-8 h-8 flex items-center justify-center cursor-pointer rounded-full bg-gray-200 hover:bg-gray-300"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  </div>

                  <form
                    onSubmit={e => {
                      e.preventDefault();
                      editingItem ? handleUpdateItem() : handleAddItem();
                    }}
                  >
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Item Name *
                        </label>
                        <input
                          type="text"
                          value={newItem.title}
                          onChange={e => setNewItem({ ...newItem, title: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                          placeholder="Enter item name"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Description *
                        </label>
                        <textarea
                          value={newItem.description}
                          onChange={e => setNewItem({ ...newItem, description: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                          placeholder="Describe the item"
                          rows={3}
                          required
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Category
                          </label>
                          <select
                            value={newItem.category}
                            onChange={e => setNewItem({ ...newItem, category: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent pr-8"
                          >
                            {categories
                              .filter(cat => cat !== 'All')
                              .map(category => (
                                <option key={category} value={category}>
                                  {category}
                                </option>
                              ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Priority
                          </label>
                          <select
                            value={newItem.priority}
                            onChange={e =>
                              setNewItem({ ...newItem, priority: e.target.value as 'low' | 'medium' | 'high' })
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent pr-8"
                          >
                            <option value="high">🔥 High Priority</option>
                            <option value="medium">🔥 Medium Priority</option>
                            <option value="low">🔥 Low Priority</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Price *
                        </label>
                        <input
                          type="text"
                          value={newItem.price}
                          onChange={e => handlePriceChange(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                          placeholder="Enter price (e.g., $29.99)"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Checkout URL *
                        </label>
                        <input
                          type="url"
                          value={newItem.checkoutUrl}
                          onChange={e => setNewItem({ ...newItem, checkoutUrl: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                          placeholder="https://example.com/product"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Tags (comma-separated)
                        </label>
                        <input
                          type="text"
                          value={newItem.tags?.join(', ') || ''}
                          onChange={e => handleTagInput(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                          placeholder="e.g., luxury, gift, fashion"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Notes
                        </label>
                        <textarea
                          value={newItem.notes}
                          onChange={e => setNewItem({ ...newItem, notes: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                          placeholder="Any additional notes about this item"
                          rows={2}
                        />
                      </div>

                      {/* Buttons */}
                      <div className="flex gap-2 justify-end">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setShowAddForm(false);
                            setEditingItem(null);
                            setNewItem({
                              title: '',
                              description: '',
                              category: 'Electronics',
                              image: '',
                              tags: [],
                              notes: '',
                              price: '',
                              priority: 'medium',
                              checkoutUrl: ''
                            });
                          }}
                        >
                          Cancel
                        </Button>
                        <Button type="submit">{editingItem ? 'Update Item' : 'Add Item'}</Button>
                      </div>
                    </div>
                  </form>
                </div>
              </Card>
            </div>
          )}
        </>
      )}

      <Footer />
    </div>
  );
}
